import { useState } from 'react';

import { useNavigate } from 'react-router';

import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { Radio, RadioGroup } from '@cfa/react-core';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';

import AddTeamMembersPopUp from '@/components/popups/AddTeamMemberPopUp/AddTeamMembersPopUp';
import ConfirmationModal from '@/components/popups/ConfirmationModal';

import Constants, { routes } from '@/constants';

import {
  getPlanAndAssignmentsOptions,
  PathwayUser,
  Plan,
} from '@/_generated/api-clients/xp';
import {
  trainingModeAtom,
  trainingModeDispatchAtom,
} from '@/atoms/trainingMode';

interface TrainTogetherProps {
  isCompliance: boolean;
  plan: Plan;
  setSelectedId: (id: string) => void;
  setShowAddTeamMembersPopup: (show: boolean) => void;
  setShowDueDatePopup: (show: boolean) => void;
}

const trainingModeModalIsOpenAtom = focusAtom(trainingModeAtom, o =>
  o.prop('isTrainingModeOpen'),
);
const setTrainingModeDataAtom = focusAtom(trainingModeAtom, o =>
  o.prop('trainingModeData'),
);

const TrainTogether: React.FC<TrainTogetherProps> = ({
  isCompliance,
  plan,
  setSelectedId,
  setShowAddTeamMembersPopup,
  setShowDueDatePopup,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const trainingModeModalIsOpen = useAtomValue(trainingModeModalIsOpenAtom);
  const sendTrainingModeAction = useSetAtom(trainingModeDispatchAtom);
  const setTrainingModeData = useSetAtom(setTrainingModeDataAtom);

  const [temporaryTeamMembers, setTemporaryTeamMembers] = useState<
    PathwayUser[]
  >([]);
  const [showSelectLanguagePopup, setShowSelectLanguagePopup] = useState(false);
  const [showTrainingModePopup, setShowTrainingModePopup] = useState(false);
  const [languageSelected, setLanguageSelected] = useState('English');
  const [trainingModeTeamMembers, setTrainingModeTeamMembers] = useState([]);
  const planId = plan.id;

  const { data: planAndAssignments } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId } }),
    enabled:
      !!plan.id &&
      !isCompliance &&
      trainingModeModalIsOpen &&
      isValidPlanId(plan.id),
  });

  const onEnterTrainingMode = () => {
    setShowTrainingModePopup(true);
    sendTrainingModeAction('hideTrainingMode');
  };

  const handleNextClick = (selectedTeamMembers: PathwayUser[]) => {
    /** We temporarily set these so they do not render until we send
     * the user to Train Together below.
     */
    setTemporaryTeamMembers(selectedTeamMembers);
    sendTrainingModeAction('hideTrainingMode');
    setShowSelectLanguagePopup(true);
  };

  const onNextClick = () => {
    if (!languageSelected) return;
    setShowSelectLanguagePopup(false);
    sendTrainingModeAction('enterTrainingMode');
    sendNativeMessage({ type: 'trainingModeActive', payload: true });
    setTrainingModeData({
      estimatedMinutes: plan.estimatedTimeToCompleteInMinutes ?? 0,
      languageSelected,
      planId: plan.id,
      planLabel: t(mapCategoryToAliasTranslation(plan.category)),
      planName: itemByLanguage(plan.name, ''),
      teamMembers: trainingModeTeamMembers,
      selectedTeamMembers: temporaryTeamMembers,
      assignedTeamMembersStatus: planAndAssignments?.assignments?.filter(
        teamMember => teamMember.status !== Constants.TRAINING_PLANS.COMPLETED,
      ),
    });
    navigate({
      pathname: routes.plans.trainTogether({ planId: plan.id }),
    });
  };

  const onClose = () => {
    setSelectedId('');
    setShowAddTeamMembersPopup(false);
    setShowDueDatePopup(false);
    setShowTrainingModePopup(false);
  };

  const onBack = () => {
    setTrainingModeTeamMembers([]);
    setShowSelectLanguagePopup(false);
    setSelectedId('');
  };
  return (
    <>
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainTogether.enterModalBody')}
        headerText={t('TrainingPlans.trainTogether.enterModalHeader')}
        isOpen={trainingModeModalIsOpen}
        onClose={() => sendTrainingModeAction('hideTrainingMode')}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onEnterTrainingMode}
        primaryButtonText={t('Button.iUnderstand')}
        secondaryButtonHandler={() =>
          sendTrainingModeAction('hideTrainingMode')
        }
        secondaryButtonText={t('Button.cancel')}
      />
      {/** Manage Plans Tab: Assign Team Members in Train Together*/}
      <AddTeamMembersPopUp
        handleNextClick={({ selectedTeamMembers }) =>
          handleNextClick(selectedTeamMembers)
        }
        isOpen={showTrainingModePopup}
        isTrainingMode={true}
        onClose={onClose}
        plan={showTrainingModePopup ? plan : undefined}
      />
      <ConfirmationModal
        bodyText={t('TrainingPlans.trainingMode.selectLanguage')}
        headerText={t('TrainingPlans.trainingMode.chooseLanguage')}
        isOpen={showSelectLanguagePopup}
        onClose={() => setShowSelectLanguagePopup(false)}
        primaryButtonColor={'secondary'}
        primaryButtonHandler={onNextClick}
        primaryButtonText={t('Button.next')}
        secondaryButtonHandler={onBack}
        secondaryButtonText={t('Button.back')}
      >
        <div className="flex cursor-pointer justify-center">
          <RadioGroup
            defaultValue={Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE}
            orientation="vertical"
            onChange={value => setLanguageSelected(value)}
          >
            <Radio value={Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE}>
              {t('Language.english')}
            </Radio>
            <Radio value={Constants.LANGUAGE.SPANISH_LANGUAGE_CODE}>
              {t('Language.spanish')}
            </Radio>
          </RadioGroup>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default TrainTogether;
